import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';
import SkillNode from './components/SkillNode';
import SkillConnection from './components/SkillConnection';
import SkillDetailPanel from './components/SkillDetailPanel';
import ConstellationControls from './components/ConstellationControls';
import SkillLegend from './components/SkillLegend';
import CosmicBackground from './components/CosmicBackground';
import Icon from '../../components/AppIcon';

const SkillConstellation = () => {
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isRotating, setIsRotating] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showLegend, setShowLegend] = useState(true);
  const constellationRef = useRef(null);

  // Mock skill data
  const skillsData = [
    // Technical Skills (Primary)
    {
      id: 'react',
      name: 'React',
      icon: 'Code',
      category: 'technical',
      level: 5,
      size: 'large',
      position: { x: 30, y: 25 },
      experience: '5+ years',
      yearsOfExperience: 5,
      isRecent: false,
      isCertified: true,
      description: `Advanced React development with deep expertise in hooks, context, performance optimization, and modern patterns. Experienced in building scalable applications with complex state management and real-time features.`,
      timeline: [
        { year: '2019', milestone: 'Started with React', context: 'First commercial project' },
        { year: '2020', milestone: 'Hooks Mastery', context: 'Migrated class components' },
        { year: '2021', milestone: 'Advanced Patterns', context: 'Custom hooks and context' },
        { year: '2022', milestone: 'Performance Expert', context: 'Large-scale optimization' },
        { year: '2024', milestone: 'React 18 Features', context: 'Concurrent features' }
      ],
      connections: ['JavaScript', 'TypeScript', 'Next.js', 'Redux'],
      resources: ['React Documentation', 'Advanced React Patterns', 'React Performance Guide']
    },
    {
      id: 'threejs',
      name: 'Three.js',
      icon: 'Box',
      category: 'technical',
      level: 4,
      size: 'large',
      position: { x: 70, y: 35 },
      experience: '3+ years',
      yearsOfExperience: 3,
      isRecent: false,
      isCertified: false,
      description: `Proficient in 3D web development using Three.js for creating immersive experiences, interactive visualizations, and complex 3D scenes with optimized performance.`,
      timeline: [
        { year: '2021', milestone: 'First 3D Project', context: 'Product visualization' },
        { year: '2022', milestone: 'Advanced Shaders', context: 'Custom material development' },
        { year: '2023', milestone: 'Performance Optimization', context: 'Large scene management' },
        { year: '2024', milestone: 'WebXR Integration', context: 'AR/VR experiences' }
      ],
      connections: ['WebGL', 'JavaScript', 'Creative Direction', 'UX Design'],
      resources: ['Three.js Documentation', 'WebGL Fundamentals', 'Shader Programming']
    },
    {
      id: 'nodejs',
      name: 'Node.js',
      icon: 'Server',
      category: 'technical',
      level: 4,
      size: 'medium',
      position: { x: 15, y: 60 },
      experience: '4+ years',
      yearsOfExperience: 4,
      isRecent: false,
      isCertified: true,
      description: `Backend development expertise with Node.js, building scalable APIs, real-time applications, and microservices architecture.`,
      connections: ['JavaScript', 'Express.js', 'MongoDB', 'API Design'],
      resources: ['Node.js Best Practices', 'Express.js Guide', 'Microservices Patterns']
    },
    {
      id: 'typescript',
      name: 'TypeScript',
      icon: 'FileCode',
      category: 'technical',
      level: 4,
      size: 'medium',
      position: { x: 45, y: 15 },
      experience: '3+ years',
      yearsOfExperience: 3,
      isRecent: false,
      isCertified: false,
      description: `Strong typing expertise for large-scale applications, advanced type systems, and developer experience improvements.`,
      connections: ['JavaScript', 'React', 'Node.js', 'Code Quality'],
      resources: ['TypeScript Handbook', 'Advanced Types', 'Type Challenges']
    },

    // Creative Skills (Primary)
    {
      id: 'ux-design',
      name: 'UX Design',
      icon: 'Users',
      category: 'creative',
      level: 5,
      size: 'large',
      position: { x: 25, y: 75 },
      experience: '6+ years',
      yearsOfExperience: 6,
      isRecent: false,
      isCertified: true,
      description: `Comprehensive user experience design with expertise in user research, information architecture, interaction design, and usability testing.`,
      timeline: [
        { year: '2018', milestone: 'UX Foundation', context: 'First design role' },
        { year: '2019', milestone: 'User Research', context: 'Qualitative methods' },
        { year: '2020', milestone: 'Design Systems', context: 'Component libraries' },
        { year: '2022', milestone: 'Accessibility Expert', context: 'WCAG compliance' },
        { year: '2024', milestone: 'AI/UX Integration', context: 'Emerging technologies' }
      ],
      connections: ['UI Design', 'User Research', 'Prototyping', 'Accessibility'],
      resources: ['Design of Everyday Things', 'UX Research Methods', 'Accessibility Guidelines']
    },
    {
      id: 'creative-direction',
      name: 'Creative Direction',
      icon: 'Compass',
      category: 'creative',
      level: 4,
      size: 'large',
      position: { x: 75, y: 65 },
      experience: '4+ years',
      yearsOfExperience: 4,
      isRecent: false,
      isCertified: false,
      description: `Strategic creative leadership combining artistic vision with business objectives, brand development, and cross-functional team coordination.`,
      connections: ['Brand Strategy', 'Visual Design', 'Team Leadership', 'Project Management'],
      resources: ['Creative Strategy Framework', 'Brand Development Guide', 'Design Leadership']
    },
    {
      id: 'ui-design',
      name: 'UI Design',
      icon: 'Palette',
      category: 'creative',
      level: 5,
      size: 'medium',
      position: { x: 55, y: 80 },
      experience: '5+ years',
      yearsOfExperience: 5,
      isRecent: false,
      isCertified: true,
      description: `Advanced interface design with expertise in design systems, visual hierarchy, and modern design principles.`,
      connections: ['UX Design', 'Design Systems', 'Figma', 'Accessibility'],
      resources: ['Design System Guidelines', 'Visual Design Principles', 'Interface Design Patterns']
    },

    // Hybrid Skills
    {
      id: 'frontend-architecture',
      name: 'Frontend Architecture',
      icon: 'Layers',
      category: 'hybrid',
      level: 4,
      size: 'medium',
      position: { x: 50, y: 45 },
      experience: '4+ years',
      yearsOfExperience: 4,
      isRecent: false,
      isCertified: false,
      description: `Bridging technical implementation with design requirements, creating scalable frontend architectures that support both developer experience and user needs.`,
      connections: ['React', 'Design Systems', 'Performance', 'Code Quality'],
      resources: ['Frontend Architecture Patterns', 'Scalable CSS', 'Component Design']
    },
    {
      id: 'webgl',
      name: 'WebGL',
      icon: 'Zap',
      category: 'hybrid',
      level: 3,
      size: 'medium',
      position: { x: 85, y: 45 },
      experience: '2+ years',
      yearsOfExperience: 2,
      isRecent: true,
      isCertified: false,
      description: `Low-level graphics programming for web, creating custom shaders and optimized 3D experiences that blend technical precision with creative vision.`,
      connections: ['Three.js', 'GLSL', 'Creative Coding', 'Performance'],
      resources: ['WebGL Fundamentals', 'Shader Programming', 'Graphics Programming']
    },

    // Supporting Technical Skills
    {
      id: 'javascript',
      name: 'JavaScript',
      icon: 'Code2',
      category: 'technical',
      level: 5,
      size: 'medium',
      position: { x: 35, y: 35 },
      experience: '7+ years',
      yearsOfExperience: 7,
      isRecent: false,
      isCertified: true,
      description: `Expert-level JavaScript with deep understanding of language fundamentals, modern ES features, and performance optimization.`,
      connections: ['React', 'Node.js', 'TypeScript', 'Web APIs'],
      resources: ['JavaScript: The Good Parts', 'You Don\'t Know JS', 'Modern JavaScript']
    },
    {
      id: 'git',
      name: 'Git & Version Control',
      icon: 'GitBranch',
      category: 'technical',
      level: 4,
      size: 'small',
      position: { x: 10, y: 40 },
      experience: '6+ years',
      yearsOfExperience: 6,
      isRecent: false,
      isCertified: false,
      description: `Advanced Git workflows, branching strategies, and collaborative development practices.`,
      connections: ['Team Collaboration', 'DevOps', 'Code Quality'],
      resources: ['Pro Git Book', 'Git Workflows', 'Advanced Git Techniques']
    },

    // Supporting Creative Skills
    {
      id: 'figma',
      name: 'Figma',
      icon: 'Figma',
      category: 'creative',
      level: 5,
      size: 'small',
      position: { x: 40, y: 85 },
      experience: '4+ years',
      yearsOfExperience: 4,
      isRecent: false,
      isCertified: true,
      description: `Advanced Figma proficiency including component systems, auto-layout, and collaborative design workflows.`,
      connections: ['UI Design', 'Design Systems', 'Prototyping'],
      resources: ['Figma Academy', 'Design Systems in Figma', 'Advanced Figma Techniques']
    },
    {
      id: 'prototyping',
      name: 'Prototyping',
      icon: 'Smartphone',
      category: 'creative',
      level: 4,
      size: 'small',
      position: { x: 65, y: 85 },
      experience: '5+ years',
      yearsOfExperience: 5,
      isRecent: false,
      isCertified: false,
      description: `Interactive prototyping for user testing and stakeholder communication using various tools and techniques.`,
      connections: ['UX Design', 'User Testing', 'Figma'],
      resources: ['Prototyping Methods', 'Interactive Design', 'User Testing Guide']
    }
  ];

  // Mock project data
  const relatedProjects = [
    { name: '3D Portfolio Platform', role: 'Full-stack Developer & Designer' },
    { name: 'Interactive Data Visualization', role: 'Frontend Architect' },
    { name: 'E-commerce Design System', role: 'Lead Designer' },
    { name: 'Real-time Collaboration Tool', role: 'Technical Lead' },
    { name: 'AR Product Configurator', role: 'Creative Technologist' }
  ];

  // Skill connections data
  const skillConnections = [
    { from: 'react', to: 'javascript', type: 'foundation', strength: 1 },
    { from: 'react', to: 'typescript', type: 'enhancement', strength: 0.8 },
    { from: 'threejs', to: 'webgl', type: 'foundation', strength: 0.9 },
    { from: 'threejs', to: 'creative-direction', type: 'synergy', strength: 0.7 },
    { from: 'ux-design', to: 'ui-design', type: 'synergy', strength: 1 },
    { from: 'ux-design', to: 'frontend-architecture', type: 'enhancement', strength: 0.6 },
    { from: 'creative-direction', to: 'ui-design', type: 'synergy', strength: 0.8 },
    { from: 'frontend-architecture', to: 'react', type: 'synergy', strength: 0.9 },
    { from: 'javascript', to: 'nodejs', type: 'foundation', strength: 0.8 },
    { from: 'figma', to: 'ui-design', type: 'enhancement', strength: 0.7 },
    { from: 'prototyping', to: 'ux-design', type: 'enhancement', strength: 0.6 }
  ];

  // Filter skills based on current filter
  const filteredSkills = skillsData?.filter(skill => 
    currentFilter === 'all' || skill?.category === currentFilter
  );

  // Filter connections based on visible skills
  const visibleSkillIds = filteredSkills?.map(skill => skill?.id);
  const filteredConnections = skillConnections?.filter(conn => 
    visibleSkillIds?.includes(conn?.from) && visibleSkillIds?.includes(conn?.to)
  );

  // Handle skill selection
  const handleSkillClick = (skill) => {
    setSelectedSkill(skill);
    setIsPanelOpen(true);
  };

  // Handle zoom
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  // Handle reset
  const handleReset = () => {
    setZoomLevel(1);
    setRotation(0);
    setCurrentFilter('all');
  };

  // Handle rotation toggle
  const handleToggleRotation = () => {
    setIsRotating(!isRotating);
  };

  // Handle mouse interactions for rotation
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e?.clientX, y: e?.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e?.clientX - dragStart?.x;
    setRotation(prev => prev + deltaX * 0.5);
    setDragStart({ x: e?.clientX, y: e?.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle wheel zoom
  const handleWheel = (e) => {
    e?.preventDefault();
    const delta = e?.deltaY > 0 ? -0.1 : 0.1;
    setZoomLevel(prev => Math.max(0.5, Math.min(2, prev + delta)));
  };

  // Auto rotation effect
  useEffect(() => {
    if (!isRotating) return;
    
    const interval = setInterval(() => {
      setRotation(prev => prev + 0.2);
    }, 50);

    return () => clearInterval(interval);
  }, [isRotating]);

  // Get skill position by id
  const getSkillPosition = (skillId) => {
    const skill = skillsData?.find(s => s?.id === skillId);
    return skill ? skill?.position : { x: 50, y: 50 };
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      <Header />
      {/* Cosmic Background */}
      <CosmicBackground isRotating={isRotating} />
      {/* Main Content */}
      <main className="relative z-10 pt-16">
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center">
          <div className="text-center mb-16 z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-headline text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6">
                Skill{' '}
                <span className="bg-gradient-to-r from-accent to-cosmic-energy bg-clip-text text-transparent">
                  Constellation
                </span>
              </h1>
              <p className="font-body text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Explore the interconnected universe of technical and creative capabilities
              </p>
            </motion.div>
          </div>

          {/* Constellation Container */}
          <div 
            ref={constellationRef}
            className="absolute inset-0 cursor-grab active:cursor-grabbing"
            style={{
              transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {/* Skill Connections */}
            {filteredConnections?.map((connection, index) => (
              <SkillConnection
                key={`${connection?.from}-${connection?.to}-${index}`}
                startPosition={getSkillPosition(connection?.from)}
                endPosition={getSkillPosition(connection?.to)}
                isActive={
                  selectedSkill?.id === connection?.from || 
                  selectedSkill?.id === connection?.to
                }
                strength={connection?.strength}
                connectionType={connection?.type}
              />
            ))}

            {/* Skill Nodes */}
            <AnimatePresence>
              {filteredSkills?.map((skill) => (
                <SkillNode
                  key={skill?.id}
                  skill={skill}
                  position={skill?.position}
                  size={skill?.size}
                  category={skill?.category}
                  isActive={selectedSkill?.id === skill?.id}
                  onClick={handleSkillClick}
                  connections={skillConnections?.filter(conn => conn?.from === skill?.id || conn?.to === skill?.id)?.map(conn => conn?.from === skill?.id ? conn?.to : conn?.from)
                  }
                />
              ))}
            </AnimatePresence>
          </div>
        </section>
      </main>
      {/* Skill Detail Panel */}
      <SkillDetailPanel
        skill={selectedSkill}
        isOpen={isPanelOpen}
        onClose={() => {
          setIsPanelOpen(false);
          setSelectedSkill(null);
        }}
        relatedProjects={relatedProjects}
      />
      {/* Constellation Controls */}
      <ConstellationControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
        onToggleRotation={handleToggleRotation}
        isRotating={isRotating}
        currentFilter={currentFilter}
        onFilterChange={setCurrentFilter}
        zoomLevel={zoomLevel}
      />
      {/* Skill Legend */}
      <SkillLegend isVisible={showLegend} />
      {/* Legend Toggle */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        onClick={() => setShowLegend(!showLegend)}
        className="fixed top-24 right-6 z-30 glass-panel p-3 rounded-lg shadow-cosmic-lg text-muted-foreground hover:text-foreground transition-colors"
      >
        <Icon name={showLegend ? "EyeOff" : "Eye"} size={20} />
      </motion.button>
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {[...Array(20)]?.map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-accent rounded-full opacity-60"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default SkillConstellation;